import { Camera, Check, FileText, Paperclip } from "lucide-react";
import { motion } from "motion/react";
import { BrowserFrame } from "@/components/blocks/frames/Frames";
import { EASE, SPRING } from "@/components/motion/motion";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import {
	formatPercent,
	StageScene,
	stageBar,
	stageItem,
	stagePop,
	stageRise,
	type UseCaseSceneProps,
} from "./StageScene";

/** Findings, risk matrix, action plan, client portal. Server renders the matrix. */
const DURATIONS = [3800, 4200, 4000, 4400] as const;

/** Gravity and frequency of each rated risk, on a 1–4 scale. */
const RISKS = {
	a: { gravity: 4, frequency: 3 },
	b: { gravity: 3, frequency: 3 },
	c: { gravity: 4, frequency: 2 },
} as const;
const PRIORITY = 12;

/** Action plan counts before and after the client's proof (sample data). */
const PLAN = { total: 27, done: 14, progress: 9, late: 4 } as const;

/**
 * QHSE consultancy: findings captured on site, risks placed in the
 * gravity × frequency matrix, the action plan and report generated, then the
 * client closing an action with its proof in the follow-up portal.
 */
export function QhseScene(props: UseCaseSceneProps) {
	const { dict, locale } = useI18n();
	const scene = dict.useCases.items.qhse.scene;
	const tag = dict.common.exampleBadge;

	const steps = [
		<BrowserFrame
			key="findings"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<div className="mb-3 flex items-end justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold">
						{scene.findings.title}
					</p>
					<p className="text-[10px] text-muted-foreground">
						{scene.findings.subtitle}
					</p>
				</div>
				<span className="flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] text-muted-foreground">
					<Camera className="size-3" />
					{scene.findings.photos}
				</span>
			</div>
			<ul className="space-y-2">
				{Object.entries(scene.findings.items).map(([key, item], index) => (
					<motion.li
						key={key}
						variants={stageItem}
						custom={index}
						className="flex items-start gap-3 rounded-lg border bg-background px-3 py-2"
					>
						<span
							className={cn(
								"mt-px shrink-0 rounded px-1.5 py-0.5 font-mono text-[8.5px] font-semibold whitespace-nowrap",
								index === 0
									? "bg-signal text-signal-foreground"
									: index === 3
										? "border text-muted-foreground"
										: "bg-foreground/[0.07]",
							)}
						>
							{item.tag}
						</span>
						<span className="min-w-0 flex-1 text-[10.5px] leading-snug">
							{item.text}
						</span>
						<span className="shrink-0 font-mono text-[9px] text-muted-foreground">
							{item.ref}
						</span>
					</motion.li>
				))}
			</ul>
		</BrowserFrame>,

		<BrowserFrame
			key="rating"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="text-sm font-semibold">{scene.rating.title}</p>
			<div className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-4">
				<div className="flex gap-2">
					<span className="flex items-center font-mono text-[8.5px] text-muted-foreground [writing-mode:vertical-rl] rotate-180">
						{scene.rating.gravity}
					</span>
					<div className="flex-1">
						<div className="relative grid aspect-square grid-cols-4 grid-rows-4 gap-1">
							{Array.from({ length: 16 }, (_, cell) => {
								const gravity = 4 - Math.floor(cell / 4);
								const frequency = (cell % 4) + 1;
								const criticality = gravity * frequency;
								return (
									<span
										key={`${gravity}-${frequency}`}
										className={cn(
											"rounded-[3px]",
											criticality >= PRIORITY
												? "bg-signal/30"
												: criticality >= 6
													? "bg-signal/12"
													: "bg-foreground/[0.05]",
										)}
									/>
								);
							})}
							{Object.entries(RISKS).map(([key, risk], index) => (
								<motion.span
									key={key}
									className="absolute flex size-[18%] items-center justify-center"
									style={{
										left: `${(risk.frequency - 1) * 25 + 3.5}%`,
										top: `${(4 - risk.gravity) * 25 + 3.5}%`,
									}}
									variants={{
										hidden: { opacity: 0, x: -40, y: 30, scale: 0.4 },
										visible: {
											opacity: 1,
											x: 0,
											y: 0,
											scale: 1,
											transition: { ...SPRING, delay: 0.3 + index * 0.3 },
										},
									}}
								>
									<span
										className={cn(
											"flex size-full items-center justify-center rounded-full font-mono text-[9px] font-semibold",
											risk.gravity * risk.frequency >= PRIORITY
												? "bg-signal text-signal-foreground ring-4 ring-signal/25"
												: "bg-foreground text-background",
										)}
									>
										{index + 1}
									</span>
								</motion.span>
							))}
						</div>
						<p className="mt-1.5 text-center font-mono text-[8.5px] text-muted-foreground">
							{scene.rating.frequency}
						</p>
					</div>
				</div>
				<ul className="space-y-2 self-center">
					{Object.entries(scene.rating.risks).map(([key, risk], index) => {
						const values = RISKS[key as keyof typeof RISKS];
						const priority = values.gravity * values.frequency >= PRIORITY;
						return (
							<motion.li
								key={key}
								variants={stageItem}
								custom={index + 1}
								className={cn(
									"rounded-lg border px-2.5 py-2",
									priority && "border-signal/60 bg-signal/10",
								)}
							>
								<p className="flex gap-1.5 text-[10px] leading-tight font-medium">
									<span className="font-mono text-signal-ink">{index + 1}</span>
									{risk.name}
								</p>
								<p className="mt-1 font-mono text-[9.5px] text-muted-foreground">
									{risk.score}
								</p>
							</motion.li>
						);
					})}
					<li className="font-mono text-[8.5px] text-signal-ink">
						{scene.rating.priority}
					</li>
				</ul>
			</div>
		</BrowserFrame>,

		<BrowserFrame
			key="plan"
			host={scene.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="text-sm font-semibold">{scene.plan.title}</p>
			<div className="mt-3 overflow-hidden rounded-lg border text-[10px]">
				<div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_3.5rem] gap-2 bg-muted/70 px-3 py-1.5 font-mono text-[8.5px] text-muted-foreground uppercase">
					<span>{scene.plan.columns.action}</span>
					<span>{scene.plan.columns.owner}</span>
					<span className="text-right">{scene.plan.columns.due}</span>
				</div>
				{Object.entries(scene.plan.rows).map(([key, row], index) => (
					<motion.div
						key={key}
						variants={stageItem}
						custom={index}
						className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_3.5rem] items-center gap-2 border-t px-3 py-2"
					>
						<span className="truncate font-medium">{row.action}</span>
						<span className="truncate text-muted-foreground">{row.owner}</span>
						<span className="text-right font-mono">{row.due}</span>
					</motion.div>
				))}
			</div>
			<motion.div
				variants={stageRise}
				custom={4}
				className="mt-4 flex items-center gap-3 rounded-lg border border-signal/50 bg-signal/10 px-3 py-2.5"
			>
				<FileText className="size-4 shrink-0 text-signal-ink" />
				<span className="min-w-0 flex-1 truncate text-[10.5px] font-medium">
					{scene.plan.report}
				</span>
				<motion.span
					variants={stagePop}
					custom={1.2}
					className="flex items-center gap-1 rounded-full bg-signal px-2 py-0.5 text-[9px] font-semibold text-signal-foreground"
				>
					<Check className="size-2.5" strokeWidth={3} />
					{scene.plan.generated}
				</motion.span>
			</motion.div>
		</BrowserFrame>,

		<BrowserFrame
			key="portal"
			host={scene.portal.host}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="truncate text-sm font-semibold">{scene.portal.title}</p>
			<div className="mt-3 flex items-center gap-4">
				<div className="relative size-[4.5rem] shrink-0">
					<svg
						aria-hidden="true"
						viewBox="0 0 64 64"
						className="size-full -rotate-90"
					>
						<circle
							cx="32"
							cy="32"
							r="26"
							fill="none"
							className="stroke-foreground/[0.08]"
							strokeWidth="7"
						/>
						<motion.circle
							cx="32"
							cy="32"
							r="26"
							fill="none"
							className="stroke-signal"
							strokeWidth="7"
							strokeLinecap="round"
							variants={{
								hidden: { pathLength: PLAN.done / PLAN.total },
								visible: {
									pathLength: (PLAN.done + 1) / PLAN.total,
									transition: { duration: 0.9, ease: EASE, delay: 1.5 },
								},
							}}
						/>
					</svg>
					<span className="absolute inset-0 flex flex-col items-center justify-center">
						<span className="font-mono text-[13px] font-semibold">
							{formatPercent(
								Math.round(((PLAN.done + 1) / PLAN.total) * 100),
								locale,
							)}
						</span>
					</span>
				</div>
				<dl className="grid flex-1 grid-cols-3 gap-2 text-center">
					{(
						[
							["done", PLAN.done + 1],
							["progress", PLAN.progress - 1],
							["late", PLAN.late],
						] as const
					).map(([key, value]) => (
						<div key={key} className="rounded-lg bg-muted/60 px-1 py-2">
							<dd
								className={cn(
									"font-mono text-[15px] font-semibold",
									key === "late" && "text-muted-foreground",
								)}
							>
								{value}
							</dd>
							<dt className="text-[8.5px] text-muted-foreground">
								{scene.portal.stats[key]}
							</dt>
						</div>
					))}
				</dl>
			</div>
			<motion.div
				variants={stageRise}
				custom={2}
				className="mt-3 rounded-lg border bg-background px-3 py-2.5"
			>
				<div className="flex items-center gap-2">
					<span className="min-w-0 flex-1 truncate text-[10.5px] font-medium">
						{scene.portal.action}
					</span>
					<motion.span
						variants={stagePop}
						custom={1.5}
						className="flex shrink-0 items-center gap-1 rounded-full bg-signal px-2 py-0.5 text-[9px] font-semibold text-signal-foreground"
					>
						<Check className="size-2.5" strokeWidth={3} />
						{scene.portal.closed}
					</motion.span>
				</div>
				<p className="mt-1.5 flex items-center gap-1.5 text-[9.5px] text-muted-foreground">
					<Paperclip className="size-3" />
					{scene.portal.proof}
				</p>
				<span className="mt-2 block h-1 overflow-hidden rounded-full bg-foreground/[0.07]">
					<motion.span
						variants={stageBar}
						custom={2}
						className="block h-full origin-left rounded-full bg-signal"
					/>
				</span>
			</motion.div>
			<p className="mt-3 font-mono text-[9px] text-muted-foreground">
				{scene.portal.nextAudit}
			</p>
		</BrowserFrame>,
	];

	return (
		<StageScene
			{...props}
			labels={Object.values(scene.labels)}
			durations={DURATIONS}
			initial={1}
			render={(step) => steps[step]}
		/>
	);
}
